import { feature, mesh } from 'topojson-client';
import worldAtlas from 'world-atlas/countries-50m.json';
import detailedWorld from 'sane-topojson/dist/world_50m.json';

const rawCountryCollection = feature(worldAtlas, worldAtlas.objects.countries);
const rawCountryBorders = mesh(
  worldAtlas,
  worldAtlas.objects.countries,
  (countryA, countryB) => countryA !== countryB
);

const crossesAntimeridian = (coordinates = []) => coordinates.some((point, index) => (
  index > 0 && Math.abs(Number(point?.[0]) - Number(coordinates[index - 1]?.[0])) > 180
));

const unwrapRing = (coordinates = []) => {
  let longitudeOffset = 0;
  let previousLongitude = Number(coordinates[0]?.[0]);
  return coordinates.map((point, index) => {
    const longitude = Number(point?.[0]);
    const latitude = Number(point?.[1]);
    if (index > 0) {
      let adjustedLongitude = longitude + longitudeOffset;
      if (adjustedLongitude - previousLongitude > 180) longitudeOffset -= 360;
      if (adjustedLongitude - previousLongitude < -180) longitudeOffset += 360;
    }
    const adjustedLongitude = longitude + longitudeOffset;
    previousLongitude = adjustedLongitude;
    return [adjustedLongitude, latitude];
  });
};

const clipRingAtLongitude = (coordinates, boundary, keepGreater) => {
  const output = [];
  coordinates.forEach((current, index) => {
    const previous = coordinates[(index + coordinates.length - 1) % coordinates.length];
    const currentInside = keepGreater ? current[0] >= boundary : current[0] <= boundary;
    const previousInside = keepGreater ? previous[0] >= boundary : previous[0] <= boundary;
    if (currentInside !== previousInside) {
      const distance = current[0] - previous[0];
      const ratio = distance === 0 ? 0 : (boundary - previous[0]) / distance;
      output.push([boundary, previous[1] + ((current[1] - previous[1]) * ratio)]);
    }
    if (currentInside) output.push(current);
  });
  return output;
};

const splitRingAtAntimeridian = (ring = []) => {
  const openRing = ring.length > 1
    && ring[0][0] === ring[ring.length - 1][0]
    && ring[0][1] === ring[ring.length - 1][1]
    ? ring.slice(0, -1)
    : ring.slice();
  const unwrapped = unwrapRing(openRing);
  const parts = [];

  for (let shift = -360; shift <= 360; shift += 360) {
    const shifted = unwrapped.map(([longitude, latitude]) => [longitude + shift, latitude]);
    const clippedLeft = clipRingAtLongitude(shifted, -179.999, true);
    const clipped = clipRingAtLongitude(clippedLeft, 179.999, false);
    if (clipped.length < 3) continue;
    clipped.push([...clipped[0]]);
    parts.push(clipped);
  }
  return parts;
};

const splitCountryFeature = (country) => {
  if (country?.properties?.name === 'Antarctica') return null;
  const geometry = country?.geometry;
  if (!geometry || !['Polygon', 'MultiPolygon'].includes(geometry.type)) return country;
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  const splitPolygons = [];

  polygons.forEach((polygon) => {
    const outerRing = polygon?.[0] || [];
    if (!crossesAntimeridian(outerRing)) {
      splitPolygons.push(polygon);
      return;
    }
    splitRingAtAntimeridian(outerRing).forEach((part) => splitPolygons.push([part]));
  });

  return {
    ...country,
    geometry: { type: 'MultiPolygon', coordinates: splitPolygons },
  };
};

const splitLineAtAntimeridian = (coordinates = []) => {
  if (coordinates.length < 2) return [];
  const parts = [];
  let currentPart = [coordinates[0]];
  for (let index = 1; index < coordinates.length; index += 1) {
    const previous = coordinates[index - 1];
    const current = coordinates[index];
    const delta = current[0] - previous[0];
    if (Math.abs(delta) <= 180) {
      currentPart.push(current);
      continue;
    }
    const eastbound = delta < -180;
    const boundary = eastbound ? 180 : -180;
    const adjustedLongitude = current[0] + (eastbound ? 360 : -360);
    const ratio = (boundary - previous[0]) / (adjustedLongitude - previous[0]);
    const latitude = previous[1] + ((current[1] - previous[1]) * ratio);
    currentPart.push([boundary, latitude]);
    if (currentPart.length > 1) parts.push(currentPart);
    currentPart = [[-boundary, latitude], current];
  }
  if (currentPart.length > 1) parts.push(currentPart);
  return parts;
};

const countryCollection = {
  ...rawCountryCollection,
  features: rawCountryCollection.features.map(splitCountryFeature).filter(Boolean),
};
const countryBorders = {
  type: 'MultiLineString',
  coordinates: (rawCountryBorders.type === 'LineString'
    ? [rawCountryBorders.coordinates]
    : rawCountryBorders.coordinates
  ).flatMap(splitLineAtAntimeridian),
};

const rawSubdivisionBorders = mesh(
  detailedWorld,
  detailedWorld.objects.subunits,
  (subdivisionA, subdivisionB) => subdivisionA !== subdivisionB
);
const subdivisionBorders = {
  type: 'MultiLineString',
  coordinates: (rawSubdivisionBorders.type === 'LineString'
    ? [rawSubdivisionBorders.coordinates]
    : rawSubdivisionBorders.coordinates
  ).flatMap(splitLineAtAntimeridian),
};
const subdivisionFeatures = feature(detailedWorld, detailedWorld.objects.subunits).features;
const lakes = feature(detailedWorld, detailedWorld.objects.lakes);
const rivers = feature(detailedWorld, detailedWorld.objects.rivers);

const COUNTRY_LABELS = [
  ['Canada', 57, -106],
  ['United States', 38, -99],
  ['Mexico', 23, -102],
  ['Brazil', -11, -52],
  ['Argentina', -37, -65],
  ['United Kingdom', 54, -3],
  ['France', 46.5, 2],
  ['Spain', 40, -4],
  ['Germany', 51, 10],
  ['Italy', 42.5, 12.5],
  ['Nigeria', 9, 8],
  ['South Africa', -29, 24],
  ['Egypt', 27, 30],
  ['Saudi Arabia', 24, 45],
  ['Russia', 61, 87],
  ['India', 22, 79],
  ['China', 36, 104],
  ['Japan', 37, 138],
  ['Indonesia', -3, 119],
  ['Australia', -25, 134],
];

const MAJOR_CITIES = [
  ['New York', 40.7128, -74.006], ['Los Angeles', 34.0522, -118.2437],
  ['Chicago', 41.8781, -87.6298], ['Miami', 25.7617, -80.1918],
  ['Toronto', 43.6532, -79.3832], ['Vancouver', 49.2827, -123.1207],
  ['Mexico City', 19.4326, -99.1332], ['São Paulo', -23.5505, -46.6333],
  ['Buenos Aires', -34.6037, -58.3816], ['Bogotá', 4.711, -74.0721],
  ['Lima', -12.0464, -77.0428], ['Santiago', -33.4489, -70.6693],
  ['London', 51.5072, -0.1276], ['Paris', 48.8566, 2.3522],
  ['Madrid', 40.4168, -3.7038], ['Berlin', 52.52, 13.405],
  ['Rome', 41.9028, 12.4964], ['Amsterdam', 52.3676, 4.9041],
  ['Istanbul', 41.0082, 28.9784], ['Moscow', 55.7558, 37.6173],
  ['Cairo', 30.0444, 31.2357], ['Lagos', 6.5244, 3.3792],
  ['Nairobi', -1.2921, 36.8219], ['Johannesburg', -26.2041, 28.0473],
  ['Cape Town', -33.9249, 18.4241], ['Dubai', 25.2048, 55.2708],
  ['Riyadh', 24.7136, 46.6753], ['Mumbai', 19.076, 72.8777],
  ['Delhi', 28.6139, 77.209], ['Bengaluru', 12.9716, 77.5946],
  ['Beijing', 39.9042, 116.4074], ['Shanghai', 31.2304, 121.4737],
  ['Hong Kong', 22.3193, 114.1694], ['Tokyo', 35.6762, 139.6503],
  ['Seoul', 37.5665, 126.978], ['Bangkok', 13.7563, 100.5018],
  ['Singapore', 1.3521, 103.8198], ['Jakarta', -6.2088, 106.8456],
  ['Manila', 14.5995, 120.9842], ['Sydney', -33.8688, 151.2093],
  ['Melbourne', -37.8136, 144.9631], ['Auckland', -36.8509, 174.7645],
];

const countryTone = (countryId, palette) => {
  const numericId = Number.parseInt(String(countryId || '0'), 10) || 0;
  return palette.land[numericId % palette.land.length];
};

export function addLocalWorldBasemap(L, map, { theme = 'light' } = {}) {
  const dark = theme === 'dark';
  const palette = dark
    ? {
        ocean: '#08111f',
        land: ['#18283a', '#1b2c3f', '#1d3044', '#172638'],
        coast: '#41536b',
        border: '#34465d',
        subdivision: '#52647c',
        lake: '#10263a',
        river: '#28516d',
      }
    : {
        ocean: '#dce8f1',
        land: ['#f8fafb', '#f4f7f9', '#eef3f6', '#f6f8fa'],
        coast: '#aebdc8',
        border: '#cbd6de',
        subdivision: '#b7c6d1',
        lake: '#d6e6f0',
        river: '#9fc6dc',
      };

  const landPaneName = 'local-world-land';
  const hydroPaneName = 'local-world-hydro';
  const borderPaneName = 'local-world-borders';
  const subdivisionPaneName = 'local-world-subdivisions';
  const labelPaneName = 'local-world-labels';
  const detailLabelPaneName = 'local-world-detail-labels';
  const panes = [
    [landPaneName, 200],
    [hydroPaneName, 203],
    [borderPaneName, 205],
    [subdivisionPaneName, 207],
    [labelPaneName, 210],
    [detailLabelPaneName, 212],
  ];

  panes.forEach(([name, zIndex]) => {
    if (map.getPane(name)) return;
    const pane = map.createPane(name);
    pane.style.zIndex = String(zIndex);
    pane.style.pointerEvents = 'none';
  });

  map.getContainer().style.backgroundColor = palette.ocean;

  const layer = L.layerGroup().addTo(map);
  const countryLabelLayer = L.layerGroup().addTo(map);
  const hydroLayer = L.layerGroup();
  const subdivisionLayer = L.layerGroup();
  const cityLayer = L.layerGroup();
  const subdivisionLabelLayer = L.layerGroup();
  let streetDetailAvailable = false;

  L.geoJSON(countryCollection, {
    pane: landPaneName,
    interactive: false,
    style: (country) => ({
      color: palette.coast,
      weight: 0.8,
      opacity: dark ? 0.9 : 0.82,
      fillColor: countryTone(country?.id, palette),
      fillOpacity: 1,
      lineJoin: 'round',
    }),
  }).addTo(layer);

  L.geoJSON(countryBorders, {
    pane: borderPaneName,
    interactive: false,
    style: {
      color: palette.border,
      weight: 0.55,
      opacity: dark ? 0.82 : 0.9,
      lineCap: 'round',
      lineJoin: 'round',
    },
  }).addTo(layer);

  COUNTRY_LABELS.forEach(([name, lat, lng]) => {
    L.marker([lat, lng], {
      pane: labelPaneName,
      interactive: false,
      keyboard: false,
      icon: L.divIcon({
        className: 'local-world-label-shell',
        html: `<span class="local-world-label">${name}</span>`,
        iconSize: [90, 18],
        iconAnchor: [45, 9],
      }),
    }).addTo(countryLabelLayer);
  });

  L.geoJSON(lakes, {
    pane: hydroPaneName,
    interactive: false,
    style: {
      color: palette.river,
      weight: 0.45,
      opacity: dark ? 0.72 : 0.68,
      fillColor: palette.lake,
      fillOpacity: 1,
    },
  }).addTo(hydroLayer);

  L.geoJSON(rivers, {
    pane: hydroPaneName,
    interactive: false,
    style: {
      color: palette.river,
      weight: 0.65,
      opacity: dark ? 0.72 : 0.65,
      lineCap: 'round',
      lineJoin: 'round',
    },
  }).addTo(hydroLayer);

  L.geoJSON(subdivisionBorders, {
    pane: subdivisionPaneName,
    interactive: false,
    style: {
      color: palette.subdivision,
      weight: 0.65,
      opacity: dark ? 0.78 : 0.86,
      dashArray: '2 2',
      lineCap: 'round',
      lineJoin: 'round',
    },
  }).addTo(subdivisionLayer);

  MAJOR_CITIES.forEach(([name, lat, lng]) => {
    L.marker([lat, lng], {
      pane: detailLabelPaneName,
      interactive: false,
      keyboard: false,
      icon: L.divIcon({
        className: 'local-city-label-shell',
        html: `<span class="local-city-label"><i></i>${name}</span>`,
        iconSize: [120, 18],
        iconAnchor: [6, 9],
      }),
    }).addTo(cityLayer);
  });

  subdivisionFeatures.forEach((subdivision) => {
    const [lng, lat] = subdivision?.properties?.ct || [];
    const code = String(subdivision?.id || '').trim();
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !code) return;
    L.marker([lat, lng], {
      pane: detailLabelPaneName,
      interactive: false,
      keyboard: false,
      icon: L.divIcon({
        className: 'local-subdivision-label-shell',
        html: `<span class="local-subdivision-label">${code}</span>`,
        iconSize: [44, 16],
        iconAnchor: [22, 8],
      }),
    }).addTo(subdivisionLabelLayer);
  });

  const setLayerVisible = (detailLayer, visible) => {
    if (visible && !map.hasLayer(detailLayer)) detailLayer.addTo(map);
    if (!visible && map.hasLayer(detailLayer)) map.removeLayer(detailLayer);
  };
  const syncProgressiveDetail = () => {
    const zoom = map.getZoom();
    const streetDetailVisible = streetDetailAvailable && zoom >= 6;
    setLayerVisible(countryLabelLayer, zoom < 5.75);
    setLayerVisible(subdivisionLayer, zoom >= 3.75 && !streetDetailVisible);
    setLayerVisible(hydroLayer, zoom >= 4.25 && !streetDetailVisible);
    setLayerVisible(cityLayer, zoom >= 4.75 && !streetDetailVisible);
    setLayerVisible(subdivisionLabelLayer, zoom >= 5.75 && !streetDetailVisible);
  };

  map.on('zoomend', syncProgressiveDetail);
  layer.setStreetDetailAvailable = (available = true) => {
    streetDetailAvailable = Boolean(available);
    syncProgressiveDetail();
  };
  syncProgressiveDetail();

  return layer;
}

class CredentialedRangeSource {
  constructor(url) {
    this.url = url;
  }

  getKey() {
    return this.url;
  }

  async getBytes(offset, length, signal, etag) {
    const headers = new Headers({
      Range: `bytes=${offset}-${offset + length - 1}`,
    });
    if (etag) headers.set('If-Match', etag);

    const response = await fetch(this.url, {
      method: 'GET',
      credentials: 'include',
      headers,
      signal,
    });
    if (!response.ok && response.status !== 206) {
      throw new Error(`Platforma street map request failed (${response.status})`);
    }

    return {
      data: await response.arrayBuffer(),
      etag: response.headers.get('etag') || undefined,
      expires: response.headers.get('expires') || undefined,
      cacheControl: response.headers.get('cache-control') || undefined,
    };
  }
}

export async function addSelfHostedStreetBasemap(L, map, {
  url,
  theme = 'light',
  minZoom = 6,
  maxZoom = 15,
} = {}) {
  if (!url) return null;

  try {
    const availabilityResponse = await fetch(url, {
      method: 'HEAD',
      credentials: 'include',
    });
    if (!availabilityResponse.ok) return null;

    const [{ PMTiles }, { leafletLayer }] = await Promise.all([
      import('pmtiles'),
      import('protomaps-leaflet'),
    ]);
    const archive = new PMTiles(new CredentialedRangeSource(url));
    const header = await archive.getHeader();
    const archiveDataMaxZoom = Math.max(minZoom, Number(header?.maxZoom) || maxZoom);
    const displayMaxZoom = Math.max(maxZoom, archiveDataMaxZoom);
    const streetPaneName = 'platforma-street-basemap';
    if (!map.getPane(streetPaneName)) {
      const streetPane = map.createPane(streetPaneName);
      streetPane.style.zIndex = '220';
      streetPane.style.pointerEvents = 'none';
    }
    const layer = leafletLayer({
      url: archive,
      flavor: theme === 'dark' ? 'dark' : 'light',
      lang: 'en',
      pane: streetPaneName,
      minZoom,
      maxZoom: displayMaxZoom,
      maxDataZoom: archiveDataMaxZoom,
      noWrap: true,
      bounds: [[7, -168], [84, -52]],
      updateWhenIdle: true,
      keepBuffer: 2,
    });
    layer.addTo(map);
    const attribution = L.control({ position: 'bottomright' });
    attribution.onAdd = () => {
      const container = L.DomUtil.create('div', 'platforma-map-data-attribution');
      container.innerHTML = '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">Map data © OpenStreetMap contributors</a>';
      return container;
    };
    attribution.addTo(map);
    map.setMaxZoom(displayMaxZoom);
    return layer;
  } catch (error) {
    console.info('[analytics-map] Private North America street archive is not available yet.', error);
    return null;
  }
}
